import { Badge, CardTitle } from "@cointervue/design-system/components";
import type { JobCardSkillsProps } from "./types";
import {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
} from "react";

export default function JobCardSkills({ skills }: JobCardSkillsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Track how many skill badges are currently allowed to render
  const [visibleCount, setVisibleCount] = useState<number>(skills?.length || 0);

  // Memoized helper to reset the visible count to the full skills length
  const reset = useCallback(
    () => setVisibleCount(skills?.length || 0),
    [skills],
  );

  // Whenever skills change, reset the count to start measuring from the full list
  useLayoutEffect(reset, [reset]);

  // Measure rendering to fit badges strictly within 2 rows (runs before browser paint to prevent flash)
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !container.children.length) return;

    // Row 1 offset top
    const firstTop = (container.children[0] as HTMLElement).offsetTop;

    // Find the first child that wraps to Row 2 to determine Row 2's offset top
    const second = Array.from(container.children).find(
      (c) => (c as HTMLElement).offsetTop > firstTop,
    ) as HTMLElement;

    // If a second row exists and the last rendered child (which is either the last skill or the '+x more' badge)
    // wraps onto Row 3 (meaning its offsetTop is strictly greater than Row 2's offsetTop):
    // Decrement the visible skills count by 1. React will immediately re-render and re-evaluate this effect.
    if (second && (container.lastChild as HTMLElement).offsetTop > second.offsetTop) {
      setVisibleCount((prev) => Math.max(0, prev - 1));
    }
  }, [skills, visibleCount]);

  // Re-measure and recalculate layout if the window resizes
  useEffect(() => {
    window.addEventListener("resize", reset);
    return () => window.removeEventListener("resize", reset);
  }, [reset]);

  if (!skills) return null;

  const displaySkills = skills.slice(0, visibleCount);
  const hiddenCount = skills.length - visibleCount;

  return (
    <>
      <CardTitle className="font-medium text-[15px]">Skill Criteria</CardTitle>
      <div ref={containerRef} className="mt-2 flex flex-wrap gap-2">
        {displaySkills.map((skill, index) => (
          <Badge
            key={`${skill}-${index}`}
            variant="outline"
            className="border-primary py-1"
          >
            {skill}
          </Badge>
        ))}
        {hiddenCount > 0 && (
          <Badge variant="outline" className="border-primary py-1 bg-muted">
            +{hiddenCount} more
          </Badge>
        )}
      </div>
    </>
  );
}
